import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import type { NurseFullRole } from "../../../utils/models";
import { getShortRole } from "../../../utils/utilFunctions";
import { CharCounter, maxCharsDefault } from "../../StaffCommunication/util/CharCounter";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { VariableInsertField } from "../../UIToolkit/Inputs/VariableInsertField";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { TEAM_SECTIONS } from "../config";
import { LockedPasswordLine } from "./LockedPasswordLine";
import { LoginStatusFilterChip } from "./LoginStatusFilterChip";
import { ResendWelcomeDone } from "./ResendWelcomeDone";
import { ResendWelcomeRecipients } from "./ResendWelcomeRecipients";
import { useResendWelcomeState } from "./useResendWelcomeState";

/**
 * A stand-in for the per-person password link, so the composer shows the shape of the last line the
 * recipient will read. The real one is minted per nurse, server-side, and never reaches this screen.
 */
const EXAMPLE_PASSWORD_LINK = "https://ihh.link/s/8Kq2vP";

type Props = {
	open: boolean;
	onClose: () => void;
	/** Re-read the roster so the Last Login column picks up the sends this dialog just made. */
	onSent: () => void;
};

export const ResendWelcomeDialog: FC<Props> = ({ open, onClose, onSent }) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const state = useResendWelcomeState({ open });

	const roleItems = useMemo<DropdownItem<NurseFullRole>[]>(
		() =>
			state.availableRoles.map((role) => ({
				value: role,
				id: role,
				searchText: role,
				// The full name is what the panel row shows (it has the width for it); the closed control
				// falls back to the short code, which is all that fits when several roles are ticked.
				label: role,
				selectedLabel: getShortRole(role),
				// The panel row's DOT — the same fill the Team rail's role dots use, so the two read as one legend.
				color: roleColor(role).dot,
				endAdornment: <Box sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}>{state.roleCounts.get(role) ?? 0}</Box>,
			})),
		[state.availableRoles, state.roleCounts, roleColor],
	);

	const sectionItems = useMemo<DropdownItem<string>[]>(
		() =>
			TEAM_SECTIONS.filter((section) => section.title !== "Unknown").map((section) => ({
				value: section.title,
				id: section.title,
				label: section.title,
				endAdornment: (
					<Box sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}>{state.sectionCounts.get(section.title) ?? 0}</Box>
				),
			})),
		[state.sectionCounts],
	);

	const poolCount = state.pool.length;
	const eligibleCount = state.eligible.length;
	// The people who would actually be sent to: selected AND still in the filtered list. The raw id set
	// can hold someone a background refresh has since dropped, which would make the label overstate the send.
	const selectedCount = state.selectedNurses.length;
	const loginNarrowed = state.loginChipShown && state.loginFilter === "never";

	// A failed load leaves every list empty, which otherwise reads exactly like a department with
	// nobody in it. Say so instead, or the admin concludes there is no one to write to.
	const subtitle = state.preflight.isError
		? t("team.resend_welcome.subtitle.load_failed")
		: loginNarrowed
			? t("team.resend_welcome.subtitle.never_logged_in", { eligible: eligibleCount, pool: poolCount })
			: t("team.resend_welcome.subtitle.all_matching", { pool: poolCount });

	/**
	 * Every warning that applies, strongest first. They describe different groups of people, so
	 * showing only one would hide a group the admin is about to send to.
	 */
	const warnings = [
		state.noContactCount > 0 ? t("team.resend_welcome.warning.no_contact", { n: state.noContactCount }) : null,
		state.alreadyInvitedTodayCount > 0 ? t("team.resend_welcome.warning.already_invited_today", { n: state.alreadyInvitedTodayCount }) : null,
		state.noMobileCount > 0 ? t("team.resend_welcome.warning.no_mobile", { n: state.noMobileCount }) : null,
	].filter((line): line is string => line !== null);

	const handleSend = () => {
		state.sendMutation.mutate(undefined, { onSuccess: onSent });
	};

	const handleDoneClose = () => {
		onClose();
	};

	if (state.result) {
		return <ResendWelcomeDone open={open} result={state.result} sentByFirstName={userInfo.first_name} onClose={handleDoneClose} />;
	}

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			dataTestId="resend-welcome-dialog"
			showCloseIcon
			maxWidth="md"
			fullWidth
			title={
				<Box>
					<Box sx={{ fontSize: FONT_SIZE.TITLE_XL, fontWeight: 600, letterSpacing: "-0.2px", color: INK.PRIMARY }}>{t("team.resend_welcome.title")}</Box>
					<Box data-testid="resend-welcome-subtitle" sx={{ mt: 0.625, fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, maxWidth: "66ch", fontWeight: 400 }}>
						{subtitle}
					</Box>
				</Box>
			}
			cancel={t("cancel")}
			cancelButtonVariant="secondary"
			confirm={t("team.resend_welcome.send", { n: selectedCount })}
			confirmButtonVariant="primary"
			// Zero recipients is nothing to send, and the in-flight guard stops a double-click texting everyone twice.
			confirmDisabled={selectedCount === 0 || state.sendMutation.isPending}
			confirmLoading={state.sendMutation.isPending}
			onConfirm={handleSend}
			body={
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					{/* Stacked on a phone: side by side, each control is ~150px and both its label and its value
					    ellipsize, so neither filter can be read let alone trusted. */}
					<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2.25, mt: 1 }}>
						<SystemDropdown
							data-testid="resend-welcome-role-filter"
							items={roleItems}
							multiple
							selectedItems={state.selectedRoles}
							onChangeMultiple={state.handleRolesChange}
							label={t("team.resend_welcome.filter.role")}
							// An empty multi-select renders blank, which reads as "not loaded yet" rather than as the
							// real, send-nothing state it is. The design names it.
							placeholder={t("team.resend_welcome.filter.none_selected")}
							formControlSx={{ flex: "1 1 0", minWidth: 0 }}
							fullWidth
						/>
						<SystemDropdown
							data-testid="resend-welcome-employment-filter"
							items={sectionItems}
							multiple
							selectedItems={state.selectedSections}
							onChangeMultiple={state.handleSectionsChange}
							label={t("team.resend_welcome.filter.employment_type")}
							placeholder={t("team.resend_welcome.filter.none_selected")}
							panelNote={t("team.resend_welcome.filter.agency_note")}
							formControlSx={{ flex: "1 1 0", minWidth: 0 }}
							fullWidth
						/>
					</Box>

					{state.loginChipShown && (
						<Box sx={{ mt: 1.75 }}>
							<LoginStatusFilterChip value={state.loginFilter} onChange={state.handleLoginFilterChange} onRemove={state.handleRemoveLoginFilter} />
						</Box>
					)}

					{/* The only filter this button can add back is the one the bin removes — so it disappears
					    while that chip is on screen rather than opening a menu with nothing in it. */}
					{!state.loginChipShown && (
						<Box sx={{ mt: 1.5 }}>
							<SystemButton
								data-testid="resend-welcome-add-filter"
								buttonVariant="borderless"
								leadingIcon={<AddOutlinedIcon sx={{ fontSize: 18 }} />}
								onClick={state.handleAddLoginFilter}
								noFullWidthMobile
								sx={{ fontWeight: 600 }}
							>
								{t("team.resend_welcome.add_filter")}
							</SystemButton>
						</Box>
					)}

					<Box
						sx={(theme) => ({
							mt: 1.75,
							border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
							borderRadius: 1,
							px: 2.125,
							py: 1.875,
						})}
					>
						<ResendWelcomeRecipients
							pool={state.pool}
							selectedNurseIds={state.selectedNurseIds}
							selectedCount={selectedCount}
							onSelectionChange={(ids) => state.setSelectedNurseIds(new Set(ids))}
							eligibleCount={eligibleCount}
							wasInvitedToday={state.wasInvitedToday}
						/>

						<Box sx={{ mt: 1.875, pt: 1.625, borderTop: `1px solid ${alpha("#000", 0.06)}` }}>
							<VariableInsertField
								dataTestId="resend-welcome-body"
								value={state.message}
								onChange={state.setMessage}
								insertLabel={t("team.resend_welcome.compose.insert")}
								ariaLabel={t("team.resend_welcome.compose.aria_label")}
								maxLength={maxCharsDefault}
								variables={[{ token: "{first_name}", label: "first_name" }]}
								footer={
									<>
										<LockedPasswordLine exampleLink={EXAMPLE_PASSWORD_LINK} />
										<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mt: 1.5, minHeight: 26 }}>
											<CharCounter text={state.message} render="always" sxProps={{ fontSize: FONT_SIZE.LABEL_SM, fontVariantNumeric: "tabular-nums" }} />
											{warnings.length > 0 && (
												<Box
													data-testid="resend-welcome-warning"
													sx={{ marginInlineStart: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, minWidth: 0 }}
												>
													{warnings.map((warning) => (
														<Box
															key={warning}
															sx={(theme) => ({
																display: "flex",
																alignItems: "center",
																gap: 0.875,
																px: 1.125,
																py: 0.5,
																borderRadius: 0.5,
																fontSize: FONT_SIZE.LABEL_SM,
																color: theme.palette.warning.dark,
																backgroundColor: alpha(theme.palette.warning.main, 0.12),
															})}
														>
															<WarningAmberRoundedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
															{warning}
														</Box>
													))}
												</Box>
											)}
										</Box>
									</>
								}
							/>
						</Box>
					</Box>
				</Box>
			}
		/>
	);
};
